import React, { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  BilderitModuleId,
  RoleNames,
  useGetBtrackAccessTokenLazyQuery,
  useGetBtrackDemoAccessTokenLazyQuery,
} from '@/generated/codegen';
import { useOptionalFilterState } from '@/hooks/useFilter';
import { useAuthState } from '@/utils/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { toast } from 'sonner';
import { SidebarMenuItem, SidebarMenuButton } from './ui/sidebar';
import { ExternalLinkIcon } from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

export const BTrackMenuItem: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const filter = useOptionalFilterState();
  const companyId = filter.value.company_id;
  const { user } = useAuthState();
  const [getBtrackAccessToken] = useGetBtrackAccessTokenLazyQuery();
  const [getBtrackDemoAccessToken, { loading }] =
    useGetBtrackDemoAccessTokenLazyQuery();
  const { isFeatureEnabled } = useFeatureFlags();
  // no puedo usar RBAC aca, porque el permiso es a nivel proyecto, pero el acceso tiene que andar a nivel compañia/todos los proyectos tambien
  const isBtrackEnabled =
    user?.roles?.some(
      (role) =>
        role?.role === RoleNames.Btrack && role?.company?.id === companyId
    ) && isFeatureEnabled(BilderitModuleId.Btrack);

  const handleBtrackClick = async () => {
    setIsDialogOpen(true);
    try {
      const { data, error: e } =
        isBtrackEnabled && companyId
          ? await getBtrackAccessToken({ variables: { company_id: companyId } })
          : await getBtrackDemoAccessToken();
      const token =
        data && 'getBtrackAccessToken' in data
          ? data?.getBtrackAccessToken
          : data?.getBtrackDemoAccessToken;
      setToken(token ?? null);
      if (e) {
        toast.error('Error fetching B-Track access token: ' + e.message, {
          duration: 5000,
        });
        setIsDialogOpen(false);
      }
    } catch (e) {
      toast.error('Error fetching B-Track access token:' + e, {
        duration: 5000,
      });
      setIsDialogOpen(false);
    }
  };

  const redirect = React.useCallback((token: string) => {
    window.open(`https://btrack.bilderit.com?token=${token}`, '_blank');
    setToken(null);
    setIsDialogOpen(false);
  }, []);

  React.useEffect(() => {
    if (isDialogOpen && token && isBtrackEnabled) {
      redirect(token);
    }
  }, [isBtrackEnabled, isDialogOpen, redirect, token]);

  return (
    <>
      <Tooltip>
        <TooltipContent className="text-center">B-Track</TooltipContent>
        <TooltipTrigger asChild>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleBtrackClick}
              disabled={!filter.value.company_id}
            >
              <>
                <img
                  src="/img/btrack_logo.png"
                  alt="B-Track"
                  className="size-4"
                />
                <span>B-Track</span>
                <ExternalLinkIcon className="ml-auto size-4" />
              </>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </TooltipTrigger>
      </Tooltip>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          {isBtrackEnabled ? (
            <DialogHeader>
              <DialogTitle>Estás siendo redirigido a B-Track</DialogTitle>
              <DialogDescription>
                Aguarda unos instantes mientras te redirigimos a B-Track
                <br />
                <br />
                <LoadingSpinner className="size-12" />
              </DialogDescription>
            </DialogHeader>
          ) : (
            <DialogHeader>
              <DialogTitle>B-Track</DialogTitle>
              <DialogDescription>
                Actualmente no tienes acceso a B-Track, la solución de
                seguimiento vehicular de Bilderit.
                <br />
                {isFeatureEnabled(BilderitModuleId.Btrack)
                  ? 'Comunicate con tu manager para obtener acceso.'
                  : 'Comunícate con nosotros para obtener acceso.'}
                <br />
                Presiona debajo para ingresar a una versión demo de B-Track.
                <br />
              </DialogDescription>
              <DialogFooter className="justify-center! mt-4">
                <Button
                  onClick={() => {
                    token && redirect(token);
                  }}
                  disabled={!token}
                >
                  {loading ? <LoadingSpinner /> : 'Ir a la demo'}
                </Button>
              </DialogFooter>
            </DialogHeader>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BTrackMenuItem;
